'use client';

/**
 * 광고 id 하나 → 카드 한 장 — 비슷한 소재 줄에서 다른 광고를 눌렀을 때 그 광고로 다이얼로그를 갈아 끼우려고 원본을 받는다.
 *
 * 받는 동안에는 열린 카드가 그대로 있고, 오면 그때 연다. 캐시는 광고 소재 조사의 단건 키와 같다.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { metaKeys } from 'src/query-keys/meta';
import { fetchAdDetail } from 'src/actions/els/ads-meta';

import { toast } from 'src/components/snackbar';

import { toAdMediaCard } from '../../../utils/search-all-media-cards';

import type { MediaCard } from '../../../utils/search-all-media-cards';

/** @param onOpen 카드가 만들어지면 부른다 */
export function useSearchAllAdCard(onOpen: (card: MediaCard) => void) {
  const [adId, setAdId] = useState('');

  const result = useQuery({
    queryKey: metaKeys.adDetail(adId),
    enabled: adId.length > 0,
    staleTime: 60_000,
    queryFn: () => fetchAdDetail({ adId }),
  });

  const creative = adId && result.data?.creative?.id === adId ? result.data.creative : null;

  useEffect(() => {
    if (!creative) return;
    setAdId('');
    onOpen(toAdMediaCard(creative));
  }, [creative, onOpen]);

  /*
    🚨 **실패하면 말하고, 들고 있던 id 를 비운다.**
      - 안 비우면 같은 광고를 다시 눌러도 상태가 안 바뀌어 **재조회가 안 걸린다** — 화면에는
        아무 일도 안 일어나고 사용자는 카드가 죽은 줄 안다. 비워 두면 다시 누르는 것이 곧 재시도다.
      - 말도 안 하면 「눌렀는데 아무 반응이 없다」가 된다. 이 화면에는 오류를 적을 자리가
        따로 없어 쪽지로 알린다(이 리포의 관례).
  */
  useEffect(() => {
    if (!result.isError) return;
    setAdId('');
    toast.error('광고 소재를 불러오지 못했습니다. 다시 눌러 주세요.');
  }, [result.isError]);

  return { openAd: setAdId, loading: adId.length > 0 && result.isPending };
}
